import React, { useContext, FC } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { NavContext } from 'Context/nav-context';
import UserAccountBar from './user-account-bar';
import { KEY } from 'Data/constants';
import { SubNavProps, IonEnter, IonLeave, IonKeyDown } from './isub-nav';

import styles from './sub-nav.module.scss';
import { formatLink } from '@/utils';
import { useDetermineBanner, useDetermineEnvironment } from '@/hooks';

export const relatedTargetEvent = (
  relatedTargetClassName: string,
  setL0: React.Dispatch<React.SetStateAction<string>>
) => {
  if (
    !relatedTargetClassName?.includes('megaNav') &&
    !relatedTargetClassName?.includes('navHeader') &&
    !relatedTargetClassName?.includes('offersNav')
  ) {
    setL0('');
  }
};

export const triggerKeyEscape = (key: string, setL0: React.Dispatch<React.SetStateAction<string>>) => {
  if (key === KEY.ESCAPE) {
    setL0('');
    return;
  }
};

export const checkL0UID = (L0: string, uid: string, setL0: React.Dispatch<React.SetStateAction<string>>) => {
  if (L0 === uid) {
    // if same uid toggle menu open or close
    setL0('');
  } else {
    // if new uid set it
    setL0(uid);
  }
};

export const getKeyValue = (e: React.KeyboardEvent<HTMLLIElement>) => {
  return e.key || e.type;
};

const SubNav: FC<SubNavProps> = ({ navigation, setFocusRef }) => {
  const { formatMessage } = useIntl();
  const {
    category: {
      uid: [uid],
      L0: [L0, setL0],
      L1: [, setL1],
    },
  } = useContext(NavContext);

  const isPhxBanner = useDetermineBanner();
  const env = useDetermineEnvironment();

  const navL0 =
    navigation &&
    navigation?.filter((item) => {
      return item.uid === uid;
    })[0];

  const onEnter = ({ setL0, setL1, uid, child }: IonEnter): void => {
    setL0(uid);
    if (child) setL1(child[0]?.uid);
  };

  const onLeave = ({ e, setL0 }: IonLeave): void => {
    const relatedTarget = e.relatedTarget?.parentNode?.parentNode?.parentNode?.className;

    relatedTargetEvent(relatedTarget, setL0);
  };

  const onKeyDown = ({ e, setL0, setL1, uid, child }: IonKeyDown): void => {
    const key = getKeyValue(e);

    triggerKeyEscape(key, setL0);

    if (key !== KEY.SPACE && key !== ' ') return;

    e.preventDefault();
    checkL0UID(L0, uid, setL0);
    if (setFocusRef) setFocusRef(e.target as HTMLAnchorElement);
    if (child) setL1(child[0]?.uid);
  };

  return (
    <nav className={styles.subNavContainer}>
      <div className={styles.navHeaderLayout}>
        <ul className={styles.navItemsLayout} role="menubar" aria-label={formatMessage({ id: 'shopMenu' })}>
          {navL0?.child?.map(({ name, uid, link, child }) => {
            return (
              <li
                className={clsx(styles.navItem, uid === L0 && styles.selectedNav)}
                id={uid}
                data-testid={uid}
                key={name}
                onKeyDown={(e) => onKeyDown({ e, setL0, setL1, uid, child })}
                onMouseEnter={(e) => onEnter({ e, setL0, setL1, uid, child })}
                onMouseLeave={(e) => onLeave({ e, setL0 })}
                role="none"
              >
                <div className={styles.navItemContent}>
                  <a
                    className={'L0'}
                    href={formatLink(link, isPhxBanner, env) ?? ''}
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    {name}
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
        <UserAccountBar />
      </div>
    </nav>
  );
};

export default SubNav;
